import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useFormContext, useWatch } from "react-hook-form";
import { Dataset, DatasetFilters, datasetsConstants } from "../../api/Datasets";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Nullable, StringIndexedDict } from "../../api/GenericTypes";
import { SessionContext } from "../../common/contexts/SessionContext";
import { LucideIcon } from "../../common/icon/LucideIcon";
import { useHistory } from "react-router-dom";
import { getBulkEditRoute } from "../../main/Routing";
import { FormErrors } from "../../common/forms/FormButtons";
import { MultiEditMappedItemObject } from "../../common/forms/MultiEditForms/common/MultiEditProvider";
import {
  checkForUnassignedValue,
  getValueOrDefault,
  useMultiEditUtils,
} from "../../common/forms/MultiEditForms/common/MultiEditUtils";
import { showtoast } from "../../common/overlays/Toasts/showtoast";
import { MultiEditErrorContainer } from "../../common/forms/MultiEditForms/common/MultiEditRenderUtils";
import {
  BulkEditDropDownWrapperProps,
  BulkEditLocationState,
  BulkEditDropDownProps,
} from "../../common/tables/MultiEdit/BulkEditDropdown/BulkEditDropDownTypes";
import { BulkEditDropDownWrapper } from "../../common/tables/MultiEdit/BulkEditDropdown/BulkEditDropDownWrapper";
import { ProjectsVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/ProjectsVirtualizedSelectForm";
import { AlertModal } from "../../common/modals/AlertModal/AlertModal";
import { useCustomTypes } from "../../Customization/CustomTypes/generics/useCustomTypes";
import {
  processValuesForTypedEntity,
  useCustomFieldDefaultValues,
} from "../../Customization/CustomTypes/generics/CustomTypeValidationUtils";
import { CustomTypesVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/CustomTypesVirtualizedSelectForm";
import { CustomTypeFormRenderer } from "../../Customization/CustomTypes/generics/CustomTypeFormRenderer";
import { Button } from "../../common/buttons/Button/Button";
import { CustomType } from "../../api/CustomTypes/CustomTypes";

export const DatasetsBulkEditDropDownWrapper = ({
  selection,
  onSuccess,
  onClose,
  filters,
  entityConstants,
}: BulkEditDropDownWrapperProps<Dataset, DatasetFilters>) => {
  return (
    <BulkEditDropDownWrapper<Dataset, DatasetFilters>
      selection={selection}
      onSuccess={onSuccess}
      onClose={onClose}
      filters={filters}
      entityConstants={entityConstants}
    >
      {(props) => <DatasetsBulkEditDropDown {...props} />}
    </BulkEditDropDownWrapper>
  );
};

const DatasetsBulkEditDropDown = ({
  entityConstants,
  items,
  consolidatedValues,
  selection,
  filters,
  onSuccess,
  onClose,
}: BulkEditDropDownProps<Dataset, DatasetFilters>) => {
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showClaimAlertModal, setShowClaimAlertModal] = useState(false);

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useFormContext<MultiEditMappedItemObject<Dataset>>();

  const history = useHistory();
  const { session, route } = useContext(SessionContext);
  // const { schemas, type, setType } = useCustomSchemas<DatasetType, DatasetTypeFilters>({
  //   entityConstants: datasetTypesConstants,
  // });

  const { types } = useCustomTypes({ entityType: "Dataset" });
  const [type, setType] = useState<CustomType>();

  const { customFieldPlaceholders, defaultValues, conditionalTypeFormSchema } = useCustomFieldDefaultValues<Dataset>(
    items,
    consolidatedValues,
    type
  );
  const isRequired = useRef(false);

  useEffect(() => {
    if (errors && !!Object.keys(errors).length) {
      showtoast(
        "error",
        <MultiEditErrorContainer errors={errors} entityDescSingular={entityConstants.entitySingular} />
      );
    }
  }, [entityConstants.entitySingular, errors]);

  const { onSubmit, loading } = useMultiEditUtils<Dataset>({
    entityConstants: entityConstants,
    showToast: true,
    onSuccessCallback: onSuccess,
  });

  const conditionalDatasetClaimFormSchema = useMemo(
    () => ({
      projects: yup.array().when([], () =>
        consolidatedValues.projects?.isEqual === false &&
        consolidatedValues.projects?.value === null &&
        consolidatedValues.projects?.hasEmpty === false
          ? yup.array().nullable().notRequired()
          : yup
              .array()
              .of(
                yup.object().shape({ id: yup.number().required().positive().integer(), name: yup.string().required() })
              )
              .min(1, "At least one project is required")
              .typeError(
                !!consolidatedValues.projects?.hasEmpty
                  ? "At least one item is missing a project"
                  : "At least one project is required"
              )
      ),
      ...conditionalTypeFormSchema,
    }),
    [consolidatedValues, conditionalTypeFormSchema]
  );

  // Conditional validation by a custom resolver
  const resolver = useCallback(
    async (data: Partial<Nullable<Dataset>>, context: any, options: any) => {
      if (isRequired.current) {
        return await yupResolver(yup.object().shape(conditionalDatasetClaimFormSchema))(data, context, options);
      } else {
        return await yupResolver(yup.object().shape(conditionalTypeFormSchema))(data, context, options);
      }
    },
    [conditionalDatasetClaimFormSchema, conditionalTypeFormSchema]
  );

  const {
    trigger,
    control,
    formState: { errors: errorsExternal, isDirty, dirtyFields },
    handleSubmit: internalSubmit,
    clearErrors,
    reset: resetExternal,
  } = useForm<Partial<Nullable<Dataset>>>({
    values: defaultValues,
    resolver: resolver,
  });

  useEffect(() => {
    resetExternal({ ...defaultValues, customType: type ?? defaultValues.customType }, { keepDirty: true });
  }, [defaultValues, resetExternal, type]);

  const beforeSubmit = useCallback(
    (data: MultiEditMappedItemObject<Dataset>, changes: Partial<Nullable<Dataset>>, claimMode?: boolean) => {
      const currentChanges = {
        ...(changes.customType !== null && { customType: checkForUnassignedValue(changes.customType) }),
        ...(changes.projects !== null && { projects: checkForUnassignedValue(changes.projects) }),
        ...(claimMode && { claimed: true, owner: session?.person ?? null }),
      } as Partial<Dataset>;

      const dirtyCustomFields = Object.fromEntries(
        Object.entries(changes.customFields ?? {}).filter(
          ([key, value]) => (dirtyFields?.customFields as StringIndexedDict)?.[key]
        )
      );

      let updates: MultiEditMappedItemObject<Dataset> = {};
      for (const [id, entity] of Object.entries(data)) {
        updates[id] = {
          ...entity,
          ...currentChanges,
          ...(currentChanges.customType?.id
            ? {
                ...processValuesForTypedEntity(
                  {
                    customType: currentChanges.customType?.id ? { id: currentChanges.customType.id } : null,
                    customFields: { ...entity.customFields, ...dirtyCustomFields },
                  } as Partial<Dataset>,
                  currentChanges.customType?.id ? types?.[currentChanges.customType.id] : undefined
                ),
              }
            : {
                customFields: undefined,
                customValues: undefined,
              }),
        };
      }
      return updates;
    },
    [session?.person, dirtyFields?.customFields, types]
  );

  const nChanges = selection.size;
  const nErrors = Object.keys(errorsExternal).length;
  const isError = !!nErrors;

  const _type = useWatch({ name: "customType", control: control });
  useEffect(() => {
    if (_type && typeof _type.id === "string" && types && Object.hasOwn(types, _type.id)) {
      setType(types[_type.id]);
    } else {
      // Clear custom field errors
      clearErrors("customFields");
    }
  }, [_type, clearErrors, types, setType]);

  return (
    <div
      className={`flex col-nowrap align-center`}
      style={{ width: "50vw", height: "fit-content", padding: "10px 15px", overflow: "auto" }}
    >
      <h3>Edit Metadata</h3>
      <div className="form form-group form-horizontal" style={{ width: "100%", height: "fit-content" }}>
        <ProjectsVirtualizedSelectForm
          id={"projects"}
          control={control}
          placeholder={getValueOrDefault(consolidatedValues?.projects)}
          horizontal
          isMulti
          allowCreateEntity
          allowUnassigned
          required={isRequired.current}
          filters={{ currentUserHasAddPermission: true }}
        />

        <CustomTypesVirtualizedSelectForm
          id={"customType"}
          control={control}
          placeholder={getValueOrDefault(consolidatedValues?.customType)}
          horizontal
          allowUnassigned
          disabled={
            consolidatedValues.customType?.isEqual !== undefined ? !consolidatedValues.customType.isEqual : false
          }
          filters={{ entityTypes: ["Dataset"] }}
        />

        {_type && types ? (
          <CustomTypeFormRenderer
            customType={types[_type.id]}
            control={control as any}
            errors={errorsExternal}
            placeholders={customFieldPlaceholders}
          />
        ) : null}
      </div>
      <FormErrors errors={errorsExternal} />

      <AlertModal
        type={`primary`}
        showModal={showAlertModal}
        setShowModal={setShowAlertModal}
        title={`Apply changes to ${selection.size} ${
          selection.size === 1 ? entityConstants.entitySingular : entityConstants.entityPlural
        }?`}
        proceedLabel={"Apply changes"}
        onProceed={async () => {
          isRequired.current = false;
          await internalSubmit(async (changes) => {
            await handleSubmit(async (data) => await onSubmit(beforeSubmit(items, changes), nChanges, reset))();
          })();
        }}
        onCancel={() => {
          setShowAlertModal(false);
          onClose?.();
        }}
        loading={loading}
      />

      {/* Claim */}
      <AlertModal
        type={`primary`}
        showModal={showClaimAlertModal}
        setShowModal={setShowClaimAlertModal}
        title={`Apply changes & claim ${selection.size} ${
          selection.size === 1 ? entityConstants.entitySingular : entityConstants.entityPlural
        }?`}
        proceedLabel={"Apply changes"}
        onProceed={async () => {
          isRequired.current = true;
          await internalSubmit(async (changes) => {
            await handleSubmit(async (data) => await onSubmit(beforeSubmit(items, changes, true), nChanges, reset))();
          })();
          isRequired.current = false;
          setShowClaimAlertModal(false);
        }}
        onCancel={() => {
          setShowClaimAlertModal(false);
          onClose?.();
        }}
        loading={loading}
      />
      <div className="flex row-nowrap align-center gap-5" style={{ width: "100%" }}>
        <div>
          <button
            className="btn btn-primary"
            onClick={() => {
              history.push({
                pathname: route(getBulkEditRoute(datasetsConstants.frontendIndexRoute)),
                state: {
                  filters: { ...filters, ids: Array.from(selection) },
                  items: items,
                } as BulkEditLocationState<Dataset, DatasetFilters>,
              });
            }}
            disabled={isSubmitting}
          >
            <LucideIcon name="table" /> Advanced edit
          </button>
        </div>
        <div className="flex row-nowrap align-center gap-5" style={{ marginLeft: "auto" }}>
          <Button
            className={"btn btn-ghost-secondary"}
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            loading={loading}
          >
            <LucideIcon name="x" /> Cancel
          </Button>
          <Button
            className={"btn btn-ghost-secondary"}
            type="button"
            onClick={() => resetExternal(defaultValues)}
            disabled={isSubmitting}
            loading={loading}
          >
            <LucideIcon name="refresh-ccw" /> Revert
          </Button>
          {consolidatedValues.claimed?.isEqual && !consolidatedValues.claimed?.value && (
            <Button
              className={`btn btn-success ${isSubmitting ? "loading" : ""}`}
              title={`Edit & claim all selected ${
                selection.size === 1 ? entityConstants.entitySingular : entityConstants.entityPlural
              }`}
              onClick={async () => {
                if (await trigger()) {
                  if (!isError) setShowClaimAlertModal(true);
                }
              }}
              // disabled={isSubmitting || isError}
              loading={isSubmitting}
            >
              {`${isSubmitting ? "Claiming..." : "Edit & claim"}`}{" "}
              {`${selection.size === 1 ? entityConstants.entitySingular : entityConstants.entityPlural}`}{" "}
              <span className="badge">{selection.size}</span>
            </Button>
          )}
          <Button
            className={`btn btn-primary ${isSubmitting ? "loading" : ""}`}
            title={`Apply changes to all selected ${
              selection.size === 1 ? entityConstants.entitySingular : entityConstants.entityPlural
            }`}
            onClick={async () => {
              if (await trigger()) {
                if (!isError) setShowAlertModal(true);
              }
            }}
            disabled={!isDirty}
            loading={isSubmitting}
          >
            {`${isSubmitting ? "Processing..." : "Edit"}`}{" "}
            {`${selection.size === 1 ? entityConstants.entitySingular : entityConstants.entityPlural}`}{" "}
            <span className="badge">{selection.size}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};
